// ── Name-first league creation ───────────────────────────────────────
//
// The whole decision behind "+ Create new league", as pure functions: what
// counts as a name, what counts as a collision, what to suggest instead, and
// what record comes out the other side.
//
// It lives here rather than inline in SettingsModal for one reason — it is the
// step where a new user's league either exists or silently doesn't, so it has
// to be unit- and mutation-testable without a rendered modal.
//
// What it deliberately does NOT do is write anything. The caller commits the
// returned record through saveLeagueObject, which is the single primitive that
// persists the league AND makes it current in one call. Splitting validation
// (here) from the atomic write (there) keeps both halves testable and keeps
// "created but not current" impossible to express.

/**
 * Collapse the cosmetic differences a user cannot see. Leading/trailing space
 * and doubled internal spaces are invisible in an input box but are very
 * visible to a Map keyed on the exact string.
 */
export function normalizeLeagueName(raw) {
  return String(raw ?? "").trim().replace(/\s+/g, " ");
}

/**
 * The existing league this name would collide with, or null.
 *
 * CASE-INSENSITIVE on purpose. Per-league stores — keepers, tier moves,
 * opponent configs — key off the exact league name, so "smithtown" and
 * "Smithtown" would be two leagues holding two different sets of work, and the
 * user would be certain they had lost theirs. Treating them as the same name
 * and refusing is the only outcome that doesn't lose something.
 */
export function findLeagueNameCollision(savedLeagues, name) {
  const key = normalizeLeagueName(name).toLowerCase();
  if (!key) return null;
  return (savedLeagues || []).find(l => normalizeLeagueName(l?.name).toLowerCase() === key) || null;
}

/**
 * "Smithtown" -> "Smithtown 2" -> "Smithtown 3".
 *
 * SUGGESTED, never applied silently. Overwriting a saved league takes its
 * keepers, its tier moves and its imported auction history with it; that is
 * not something to do because two leagues happened to share a name.
 */
export function suggestLeagueName(savedLeagues, name) {
  const base = normalizeLeagueName(name);
  if (!base) return "";
  if (!findLeagueNameCollision(savedLeagues, base)) return base;
  for (let n = 2; n <= 999; n++) {
    const candidate = `${base} ${n}`;
    if (!findLeagueNameCollision(savedLeagues, candidate)) return candidate;
  }
  return base;
}

export const CREATE_EMPTY_MESSAGE = "Give your league a name first.";
export const createCollisionMessage = (existingName, suggestion) =>
  `You already have a league called "${existingName}". Use "${suggestion}" instead?`;

/**
 * The create decision, in one call.
 *
 *   { ok: false, reason: "empty" | "collision", message, suggestion? }
 *   { ok: true,  league }
 *
 * ok:true carries the COMPLETE record — name and settings together in one
 * object — so a caller has nothing partial to commit.
 */
export function createLeague(savedLeagues, rawName, shape) {
  const name = normalizeLeagueName(rawName);
  if (!name) return { ok: false, reason: "empty", message: CREATE_EMPTY_MESSAGE };
  const clash = findLeagueNameCollision(savedLeagues, name);
  if (clash) {
    // Suffix the EXISTING league's name, not the string the user just typed.
    // They collided because the two names are the same name; typing
    // "smithTOWN" against a saved "Smithtown" and getting "smithTOWN 2" would
    // leave two leagues sitting next to each other in the picker spelled
    // differently — the exact confusion the case-insensitive match exists to
    // prevent, reintroduced by the fix for it.
    const suggestion = suggestLeagueName(savedLeagues, clash.name);
    return {
      ok: false, reason: "collision", suggestion,
      message: createCollisionMessage(clash.name, suggestion),
    };
  }
  // `name` last: the settings shape must never be able to rename the league.
  return { ok: true, league: { ...shape, name } };
}

/**
 * The same decision, for a league that ALREADY EXISTS — pressing Back from
 * step 2 of the create stepper onto the name step.
 *
 *   { ok: false, reason: "empty" | "collision", message, suggestion? }
 *   { ok: true,  name, renamed }
 *
 * `renamed: false` is the common case: they went back to look, not to change
 * anything. The caller advances and writes nothing.
 *
 * ⚠️ SCOPE. This is a RECORD rename and nothing more. Per-league stores —
 * keepers, tier moves, opponent configs, watchlists, draft values — all key off
 * the league name, and none of them are migrated here. That is safe for the one
 * caller that exists because a league on step 2 of its own creation is seconds
 * old and every one of those stores is still empty: reaching any of them means
 * leaving the modal, which ends the stepper for good (reopening gives the
 * tabs, not a resumed wizard). Do NOT reuse this for a general "rename my
 * league" affordance without moving those stores with it.
 *
 * The collision set EXCLUDES the league being renamed, or keeping your own name
 * would collide with yourself — and so would fixing its capitalisation.
 */
export function renameLeague(savedLeagues, fromName, rawName) {
  const name = normalizeLeagueName(rawName);
  if (!name) return { ok: false, reason: "empty", message: CREATE_EMPTY_MESSAGE };
  if (name === fromName) return { ok: true, name, renamed: false };
  const others = (savedLeagues || []).filter(l => l?.name !== fromName);
  const clash = findLeagueNameCollision(others, name);
  if (clash) {
    const suggestion = suggestLeagueName(others, clash.name);
    return {
      ok: false, reason: "collision", suggestion,
      message: createCollisionMessage(clash.name, suggestion),
    };
  }
  return { ok: true, name, renamed: true };
}
